import React, { useContext, useRef, useState } from "react";
import {
  Pressable,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
  StyleSheet,
} from "react-native";
import { mixpanel } from "../../../../utils/mixpanel";
import { supabase } from "../../../../utils/supabase";
import { SessionContext } from "../../../../contexts";
import {
  TPostUsersResquest,
  createAClinic,
  postUsers,
} from "../../../../services/api";
import {
  TPostClinicProfileRequest,
  postClinicProfile,
} from "../../../../services/api-clinic";
import {
  Input,
  NormalText,
  PrimaryButton,
  TitleText,
} from "../../../common/native/index";
import { TPrimaryGoal, TSession } from "../../../../utils/types";
import {
  clearMarketingHandoffContext,
  getMarketingHandoffContext,
} from "../../../../utils/marketingHandoff";

type FillOutClinicInfoModalProps = {
  onClose: () => void;
};

type TGoalOption = {
  value: TPrimaryGoal;
  label: string;
};

const GOAL_OPTIONS: TGoalOption[] = [
  {
    value: "lead_capture",
    label: "Capture leads from my website",
  },
  {
    value: "in_office_simulations",
    label: "Show simulations during in-office consultations",
  },
  {
    value: "other",
    label: "Other",
  },
];

const AVAILABLE_PRIMARY_GOALS = new Set<TPrimaryGoal>(
  GOAL_OPTIONS.map((goalOption) => goalOption.value),
);

const normalizeWebsiteUrl = (value: string): string | null => {
  const trimmedValue = value.trim();
  if (!trimmedValue) return null;

  const withProtocol = /^https?:\/\//i.test(trimmedValue)
    ? trimmedValue
    : `https://${trimmedValue}`;

  try {
    const parsedUrl = new URL(withProtocol);
    const isHttpProtocol =
      parsedUrl.protocol === "https:" || parsedUrl.protocol === "http:";
    const hasLikelyHost =
      parsedUrl.hostname.includes(".") || parsedUrl.hostname === "localhost";

    if (!isHttpProtocol || !hasLikelyHost) return null;
    return parsedUrl.toString();
  } catch {
    return null;
  }
};

const FillOutClinicInfoModal: React.FC<FillOutClinicInfoModalProps> = ({
  onClose,
}) => {
  const session = useContext(SessionContext)?.session;
  const [currentStep, setCurrentStep] = useState<
    "setUpAccountMethod" | "createNewClinic" | "joinExistingClinic"
  >("setUpAccountMethod");
  const [loading, setLoading] = useState<boolean>(false);
  const [clinicName, setClinicName] = useState<string>("");
  const [inviteCode, setInviteCode] = useState<string>("");
  const [primaryGoal, setPrimaryGoal] = useState<TPrimaryGoal | "">("");
  const [websiteUrl, setWebsiteUrl] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const hasInitializedFromMarketingHandoff = useRef(false);

  const isLeadCaptureGoal = primaryGoal === "lead_capture";

  React.useEffect(() => {
    if (hasInitializedFromMarketingHandoff.current) return;
    hasInitializedFromMarketingHandoff.current = true;

    const searchParams =
      typeof window !== "undefined"
        ? new URLSearchParams(window.location.search)
        : undefined;
    const marketingHandoff = getMarketingHandoffContext(searchParams);
    if (!marketingHandoff) return;

    if (
      marketingHandoff.primaryGoal &&
      AVAILABLE_PRIMARY_GOALS.has(marketingHandoff.primaryGoal)
    ) {
      setPrimaryGoal(marketingHandoff.primaryGoal);
    }
    if (marketingHandoff.websiteUrl) {
      setWebsiteUrl(marketingHandoff.websiteUrl);
    }
  }, []);

  const refreshToken = async () => {
    const { data, error } = await supabase.auth.refreshSession();
    if (error) throw new Error("Error refreshing session");
    return data;
  };

  const handleCreateNewClinicSubmit = async () => {
    setLoading(true);
    if (!session || !session.accessToken) {
      setLoading(false);
      return;
    }

    try {
      if (
        clinicName.trim().length < 3 ||
        clinicName.length > 50 ||
        !/^[a-zA-Z0-9\s-]+$/.test(clinicName)
      ) {
        setErrorMessage("Invalid clinic name.");
        setLoading(false);
        return;
      }

      if (!primaryGoal) {
        setErrorMessage("Select a goal to continue.");
        setLoading(false);
        return;
      }

      const normalizedWebsiteUrl = websiteUrl.trim()
        ? normalizeWebsiteUrl(websiteUrl)
        : null;

      if (isLeadCaptureGoal && !normalizedWebsiteUrl) {
        setErrorMessage("Please enter a valid website URL.");
        setLoading(false);
        return;
      }

      const createClinicResponse = await createAClinic(
        session.accessToken,
        clinicName,
        primaryGoal,
      );
      const createdClinicId =
        createClinicResponse?.id?.toString?.() ||
        createClinicResponse?.clinic_id?.toString?.() ||
        "";

      if (createdClinicId && primaryGoal) {
        const clinicProfileBody: TPostClinicProfileRequest = {
          primary_goal: primaryGoal,
          ...(normalizedWebsiteUrl
            ? { website_url: normalizedWebsiteUrl }
            : {}),
        };

        await postClinicProfile(
          {
            ...(session as TSession),
            clinicId: createdClinicId,
          },
          clinicProfileBody,
        );
      }

      await refreshToken();
      mixpanel.track("Signup Complete", {
        "User Email": session.userEmail,
        "Clinic Id": createdClinicId,
        "Clinic Name": clinicName,
        "Primary Goal": primaryGoal,
        "Website URL Provided": Boolean(websiteUrl.trim()),
      });

      clearMarketingHandoffContext();
      setLoading(false);
      window.location.reload();
      onClose();
    } catch {
      setErrorMessage("Error setting up account. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleJoinExistingClinicSubmit = async () => {
    setLoading(true);
    if (!session) {
      setLoading(false);
      return;
    }
    try {
      const body: TPostUsersResquest = {
        supabase_uuid: session.userId,
        invite_code: inviteCode.trim(),
        is_patient_user: false,
      };
      const joinedUser = await postUsers(session, body);
      mixpanel.track("Signup Complete", {
        "User Email": session.userEmail,
        "Clinic Id": joinedUser.clinic_uuid,
        "Clinic Name": clinicName,
      });
      clearMarketingHandoffContext();
      setLoading(false);
      window.location.reload();
      onClose();
    } catch (error: any) {
      setErrorMessage(
        error.message.includes("403")
          ? "Invalid invite code. Please try again."
          : "Error setting up account. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoalSelection = (goal: TPrimaryGoal) => {
    setPrimaryGoal(goal);
    if (goal !== "lead_capture") {
      setWebsiteUrl("");
    }
    setErrorMessage("");
    mixpanel.track("Signup Goal Selected", {
      goal,
    });
  };

  const renderStepContent = () => {
    if (currentStep === "setUpAccountMethod") {
      return (
        <View style={styles.stepContainer}>
          <NormalText text="Pick an option below:" fontWeight="600" />
          {["Create a new clinic", "Join an existing clinic"].map(
            (text, index) => (
              <TouchableOpacity
                key={index}
                style={styles.optionButton}
                onPress={() => {
                  setErrorMessage("");
                  setCurrentStep(
                    index === 0 ? "createNewClinic" : "joinExistingClinic",
                  );
                }}
              >
                <NormalText text={text} fontWeight="600" />
              </TouchableOpacity>
            ),
          )}
        </View>
      );
    }

    return (
      <View style={styles.stepContainer}>
        <View style={styles.headerBox}>
          <NormalText
            text={
              currentStep === "createNewClinic"
                ? "Create a new clinic"
                : "Join an existing clinic"
            }
            fontWeight="600"
          />
        </View>

        <Input
          name={
            currentStep === "createNewClinic" ? "Clinic Name" : "Invite Code"
          }
          value={currentStep === "createNewClinic" ? clinicName : inviteCode}
          placeholder={
            currentStep === "createNewClinic"
              ? "Enter a name for your clinic"
              : "Enter your invite code"
          }
          onChangeText={
            currentStep === "createNewClinic" ? setClinicName : setInviteCode
          }
          required
        />

        {currentStep === "createNewClinic" && (
          <>
            <NormalText
              text="What do you want to do first?"
              fontWeight="600"
              style={{ marginTop: 4 }}
            />
            <View style={styles.goalOptionsContainer}>
              {GOAL_OPTIONS.map((goalOption) => {
                const isSelected = primaryGoal === goalOption.value;
                return (
                  <Pressable
                    key={goalOption.value}
                    style={[
                      styles.goalOption,
                      isSelected && styles.goalOptionSelected,
                    ]}
                    onPress={() => handleGoalSelection(goalOption.value)}
                  >
                    <View style={styles.goalOptionRadioOuter}>
                      {isSelected ? (
                        <View style={styles.goalOptionRadioInner} />
                      ) : null}
                    </View>
                    <Text style={styles.goalOptionText}>
                      {goalOption.label}
                    </Text>
                  </Pressable>
                );
              })}
            </View>

            {isLeadCaptureGoal && (
              <Input
                name="Website URL"
                value={websiteUrl}
                placeholder="https://your-clinic-site.com"
                onChangeText={(value) => {
                  setWebsiteUrl(value);
                  setErrorMessage("");
                }}
                autoCapitalize="none"
                autoCorrect={false}
                required
              />
            )}
          </>
        )}

        {errorMessage !== "" && (
          <Text style={styles.errorText}>{errorMessage}</Text>
        )}

        {currentStep === "joinExistingClinic" && (
          <View style={styles.infoBox}>
            <NormalText text="Where to find invite code?" />
            <Text style={styles.infoText}>
              1. Account holder logs into {"\n"}
              <Text style={styles.linkText}>dashboard.clinicos.ai</Text>
              {"\n"}2. Press Account icon on top right {"\n"}3. Copy the Invite
              code
            </Text>
          </View>
        )}

        <PrimaryButton
          text="Done"
          fullWidth
          onPress={
            currentStep === "createNewClinic"
              ? handleCreateNewClinicSubmit
              : handleJoinExistingClinicSubmit
          }
          disabled={
            currentStep === "createNewClinic"
              ? clinicName.trim().length < 3 ||
                !primaryGoal ||
                (isLeadCaptureGoal && websiteUrl.trim().length === 0)
              : inviteCode.trim().length === 0
          }
          loading={loading}
        />
      </View>
    );
  };

  return (
    <View style={styles.modalContainer}>
      <TitleText text="Set Up Your Account" style={styles.titleText} />
      {currentStep === "setUpAccountMethod" ? (
        renderStepContent()
      ) : (
        <ScrollView
          style={styles.scrollContainer}
          contentContainerStyle={styles.scrollContentContainer}
          showsVerticalScrollIndicator={false}
        >
          {renderStepContent()}
        </ScrollView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  modalContainer: {
    width: "100%",
    maxWidth: 465,
    maxHeight: 700,
    padding: 24,
    gap: 24,
    borderRadius: 10,
    backgroundColor: "#fff",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
    alignItems: "center",
  },
  titleText: { color: "#00759B" },
  scrollContainer: {
    width: "100%",
  },
  scrollContentContainer: {
    flexGrow: 1,
  },
  stepContainer: { width: "100%", gap: 14 },
  optionButton: {
    borderRadius: 10,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#C4C6CD",
    paddingHorizontal: 10,
    paddingVertical: 14,
    minHeight: 60,
  },
  headerBox: {
    borderRadius: 10,
    justifyContent: "center",
    alignItems: "center",
    padding: 12,
    borderWidth: 1,
    borderColor: "#C4C6CD",
    backgroundColor: "#C4EEFD",
  },
  errorText: { color: "red", fontSize: 14 },
  infoBox: { gap: 10 },
  infoText: { fontSize: 15, lineHeight: 24, color: "#282B33" },
  linkText: { color: "#00B7F4" },
  goalOptionsContainer: {
    gap: 8,
  },
  goalOption: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#C4C6CD",
    paddingHorizontal: 12,
    paddingVertical: 10,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    minHeight: 44,
  },
  goalOptionSelected: {
    backgroundColor: "#F1FAFF",
    borderColor: "#00B7F4",
  },
  goalOptionText: {
    flex: 1,
    fontFamily: "Urbanist500",
    fontSize: 16,
    lineHeight: 22,
    color: "#282B33",
  },
  goalOptionRadioOuter: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: "#00B7F4",
    alignItems: "center",
    justifyContent: "center",
  },
  goalOptionRadioInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: "#00B7F4",
  },
});

export default FillOutClinicInfoModal;
